import Edit from '@rover/icons/svg/edit.svg';
import Map from '@rover/icons/svg/map.svg';
import NowBolt from '@rover/icons/svg/now-bolt.svg';
import Person from '@rover/icons/svg/person.svg';
import Search from '@rover/icons/svg/search.svg';
import Time from '@rover/icons/svg/time.svg';
import TextWithSingleInlineLink from '@rover/kibble/official-patterns/TextWithSingleInlineLink';
import { isTaggedParagraphText } from '@rover/kibble/official-patterns/TextWithSingleInlineLink/TextWithSingleInlineLink.common';
import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import { HeaderIconEnum } from './builders/constants';

export type SearchSectionHeaderProps = {
  id?: string;
  headerIconName?: string;
  headerText: string;
  infoIconOnClick?: () => void;
  primarySubheaderText?: string;
  secondarySubheaderText?: string;
  alertText?: string;
  alertSeverity?: AlertSeverity;
  alertLinkHref?: string;
  alertLinkEvent?: AnalyticsEvent;
};

export const getIcon = (iconName: SearchSectionHeaderProps['headerIconName']): SvgIcon => {
  switch (iconName) {
    case HeaderIconEnum.EDIT:
      return Edit;
    case HeaderIconEnum.MAP:
      return Map;
    case HeaderIconEnum.BOLT:
      return NowBolt;
    case HeaderIconEnum.PERSON:
      return Person;
    case HeaderIconEnum.TIME:
      return Time;
    case HeaderIconEnum.SEARCH:
    default:
      return Search;
  }
};

export const getAlertElement = (
  alertText: SearchSectionHeaderProps['alertText'],
  alertLinkHref: SearchSectionHeaderProps['alertLinkHref'],
  alertLinkEvent: SearchSectionHeaderProps['alertLinkEvent'],
  getTextElementFunction: (string) => React.ReactNode
): React.ReactNode => {
  if (!alertText) {
    return null;
  }

  if (!isTaggedParagraphText(alertText)) {
    // Text does not have link
    return getTextElementFunction(alertText);
  }

  const onAlertLinkClick = (): void => {
    if (alertLinkEvent) {
      emitAnalyticsEvent(alertLinkEvent);
    }
  };

  // Text has link. Note: Markdown is not supported
  return (
    <TextWithSingleInlineLink
      size="100"
      textColor="secondary"
      target="_blank"
      asParagraph
      paragraphText={alertText}
      href={alertLinkHref || ''}
      onClick={onAlertLinkClick}
      mb="0x"
    />
  );
};

/**
 * Helper function to build the test ID. This allows us to more easily pull the
 * header based on the correct icon and type of header.
 */
export const buildTestId = (
  id: SearchSectionHeaderProps['id'],
  iconName: SearchSectionHeaderProps['headerIconName']
): string => {
  if (!id) {
    return 'search-results-header';
  }

  if (iconName) {
    return `${id}-${iconName.toLowerCase()}`;
  }

  return id;
};
